import { Dayjs } from 'dayjs';

import type { CalendarEvent } from 'src/Components/Calendars/MonthCalendar/MonthCalendar.types';
import { getEventVisual } from 'src/Utils/eventVisuals';

export interface ListDay {
  date: Dayjs;
  markers: CalendarEvent[];
  events: CalendarEvent[];
}

export interface ListWeek {
  weekStart: Dayjs;
  ongoingStays: CalendarEvent[];
  reminders: CalendarEvent[];
  days: ListDay[];
  isFree: boolean;
}

const spansDay = (event: CalendarEvent, day: Dayjs): boolean => {
  const dayStart = day.startOf('day');
  const dayEnd = day.endOf('day');
  if (event.endTime) return event.startTime.isBefore(dayEnd) && event.endTime.isAfter(dayStart);
  return event.startTime.isSame(day, 'day');
};

const startsOnDay = (event: CalendarEvent, day: Dayjs): boolean =>
  event.startTime.isSame(day, 'day');

/**
 * A stay is "ongoing" for a week when it already covers that week's first day
 * without starting on it: it began earlier and still runs into the week (R4).
 */
const isOngoingAtWeekStart = (event: CalendarEvent, weekStart: Dayjs): boolean =>
  spansDay(event, weekStart) && !startsOnDay(event, weekStart);

const chunkIntoWeeks = (dates: Dayjs[]): Dayjs[][] => {
  const weeks: Dayjs[][] = [];
  for (let i = 0; i < dates.length; i += 7) {
    const chunk = dates.slice(i, i + 7);
    if (chunk.length > 0) weeks.push(chunk);
  }
  return weeks;
};

/**
 * Turns the flat day/event lists into agenda-style weeks.
 *
 * Rules implemented here (see the Calendrier v2.1 brief):
 * - R3 a stay is placed on its arrival day only; if it started before the week
 *   it is surfaced through `ongoingStays` instead of a repeated card.
 * - R4 `ongoingStays` holds the stays already running on the week's first day.
 * - R5 `isFree` is true only when the week has no stay and no dated event.
 * - R6 days without a stay or dated event are dropped; markers ride along on
 *   days that survive.
 */
const buildListWeeks = (dates: Dayjs[], events: CalendarEvent[]): ListWeek[] =>
  chunkIntoWeeks(dates).map(weekDates => {
    const weekStart = weekDates[0];

    const reminders = events.filter(
      event =>
        getEventVisual(event).kind === 'reminder' && weekDates.some(date => spansDay(event, date))
    );

    const ongoingStays = events.filter(
      event => getEventVisual(event).kind === 'ribbon' && isOngoingAtWeekStart(event, weekStart)
    );

    const days = weekDates
      .map(date => {
        const dayEvents = events
          .filter(event => {
            const { kind } = getEventVisual(event);
            // R3 — a stay only ever renders on the day it starts.
            if (kind === 'ribbon') return startsOnDay(event, date);
            if (kind === 'chip') return spansDay(event, date);
            return false;
          })
          .sort((a, b) => {
            const aRibbon = getEventVisual(a).kind === 'ribbon';
            const bRibbon = getEventVisual(b).kind === 'ribbon';
            if (aRibbon !== bRibbon) return aRibbon ? -1 : 1;
            return a.startTime.diff(b.startTime);
          });

        const markers = events.filter(
          event => getEventVisual(event).kind === 'marker' && spansDay(event, date)
        );

        return { date, markers, events: dayEvents };
      })
      // R6 — an empty day is not rendered, and takes its markers with it.
      .filter(day => day.events.length > 0);

    // R5 — reminders and markers never make a week "busy".
    const isFree = days.length === 0 && ongoingStays.length === 0;

    return { weekStart, ongoingStays, reminders, days, isFree };
  });

export default buildListWeeks;
